import * as L from 'leaflet';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject } from 'rxjs';
import { Legend } from './legend';
import { LegendDisplay } from './legendDisplay.interface';
import { StackedLegendDisplay } from './stackedLegendDisplay';
import { AbstractControl } from '../abstractControl/abstractControl';
import { MapLayer } from '../../layers/mapLayer.abstract';

export class LegendControl extends AbstractControl {
  protected legendDisplay: LegendDisplay = new StackedLegendDisplay();

  protected legendHeader: HTMLElement;
  protected legendContent: HTMLElement;

  protected http: HttpClient;

  protected layers = new Array<MapLayer>();

  protected currentLegendArraySource = new BehaviorSubject<Array<Legend>>(new Array<Legend>());

  protected selectedLegend: Legend;

  public setDisplay(legendDisplay: LegendDisplay): this {
    this.legendDisplay = legendDisplay;
    return this;
  }

  public setHttpClient(http: HttpClient): void {
    this.http = http;
  }

  public onAdd(): HTMLElement {
    return this.getControlContainer();
  }

  public init(): void {
    // set up display updater
    this.watchLegends().subscribe(() => {
      this.legendDisplay.displayLegend(
        this.getLegends(),
        (contentElement: HTMLElement, headerElements?: Array<HTMLElement>) => {
          this.setControlContent(contentElement, headerElements);
        },
      );
    });
  }

  public updateLayers(layers: Array<MapLayer>): void {
    this.layers = layers;
    this.updateLegends();
  }

  public setLegends(legends: Array<Legend>): void {
    return this.currentLegendArraySource.next(legends != null ? legends : new Array<Legend>());
  }
  public getLegends(): Array<Legend> {
    return this.currentLegendArraySource.getValue();
  }
  public watchLegends(): Observable<Array<Legend>> {
    return this.currentLegendArraySource.asObservable();
  }
  public removeLegends(layerId: string): void {
    const legendsArray = this.getLegends().filter((legendData: Legend) => {
      return legendData.layerId !== layerId;
    });
    this.setLegends(legendsArray);
  }

  public setControlContent(contentElement: HTMLElement, headerElements = new Array<HTMLElement>()): void {
    if (this.legendContent != null) {
      while (this.legendContent.childElementCount > 0) {
         
        this.legendContent.removeChild(this.legendContent.lastChild!);
      }
      this.legendContent.appendChild(contentElement);
    }

    if (this.legendHeader != null) {
      while (this.legendHeader.childElementCount > 0) {
         
        this.legendHeader.removeChild(this.legendHeader.lastChild!);
      }
      if (this.getLegends().length > 0) {
        headerElements.forEach((element: HTMLElement) => {
          this.legendHeader.appendChild(element);
        });
      } else {
        const titleDisplay = document.createElement('div');
        titleDisplay.innerHTML = 'No Legends';
        titleDisplay.classList.add('legend-title');
        this.legendHeader.appendChild(titleDisplay);
      }
    }
  }

  protected updateLegends(): void {
    const legendPromiseArray = Array<Promise<null | Array<Legend>>>();
    // get legend data for visible layers
    this.layers.forEach((layer: MapLayer) => {
      if (!layer.hidden.get()) {
        const layerLegendArray = this.getLegends().filter((legendData: Legend) => {
          return legendData.layerId === layer.id;
        });
        if (layerLegendArray.length === 0) {
          // fetch by calling
          legendPromiseArray.push(layer.getLegendData(this.http));
        } else {
          // populate from previous data
          legendPromiseArray.push(Promise.resolve(layerLegendArray));
        }
      }
    });
    void Promise.all(legendPromiseArray).then((layerLegendArrayArray: Array<Array<Legend>>) => {
      let flattenedLegendArray = new Array<Legend>();
      layerLegendArrayArray.forEach((thisLayerLegendArray: Array<Legend>) => {
        if (thisLayerLegendArray != null) {
          flattenedLegendArray = flattenedLegendArray.concat(
            thisLayerLegendArray.filter((legend: Legend) => legend != null),
          );
        }
      });
      this.setLegends(flattenedLegendArray);
    });
  }

  protected getControlContainer(): HTMLElement {
    const contentWrapper = L.DomUtil.create('div');
    contentWrapper.classList.add('legend-wrapper');
    contentWrapper.classList.add(this.legendDisplay.cssClassName);

    this.legendHeader = document.createElement('div');
    this.legendHeader.classList.add('legend-header');
    contentWrapper.appendChild(this.legendHeader);

    this.legendContent = document.createElement('div');
    this.legendContent.classList.add('legend-content');
    this.legendContent.classList.add('pretty-scroll');
    contentWrapper.appendChild(this.legendContent);

    return super.getControlContainer('legend-control', 'fas fa-th-list', 'Legends', contentWrapper);
  }
}
